import useStartDeccaConversation from '../../hooks/useStartDeccaConversation'
import useEffectBenchmark from '../../hooks/useEffectBenchmark'
import InsightCard from './InsightCard'
import { effectBenchmarkState, publicationBlockedReason } from '../../helpers/intelligence/effectBenchmarkGate'
import buildInsightDraftBrief from '../../helpers/intelligence/buildInsightDraftBrief'
import {
    saveInsightDraft, publishInsight, unpublishInsight, rejectInsight, deleteInsight,
} from '../../helpers/intelligence/intelligenceWrites'

/**
 * De redactie van één inzicht: de opzet-knop van Decca en de kaart met het
 * tekstvak en opslaan/publiceren. Eén component, twee plekken (het celdetail
 * en de eigen pagina van het inzicht), zodat de twee niet uiteenlopen.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * Een opzet komt uit de chat, zoals bij de TOC: Decca leest de cel
 * (get_insight_brief), schrijft, en zet na akkoord de tekst in het tekstvak
 * (save_insight_text, met kaart). Alleen op een concept; een publicatie
 * eerst depubliceren.
 *
 * @param {object} props
 * @param {object} props.insight - het QuestionInsights-document (live)
 * @param {string} props.themeTitle - de naam van het thema, voor het gesprek met Decca; leeg = nog niet bekend, dan geen Decca-knop
 * @param {() => void} props.onDeleted - na een zachte verwijdering (de kaart bestaat dan niet meer)
 * @param {boolean} [props.showOutput] - Toon de uitkomst van de cel op de kaart; uit op het celdetail
 */
const InsightEditor = ({ insight, themeTitle, onDeleted, showOutput = true }) => {
    const startDecca = useStartDeccaConversation()

    // De publicatiepoort op het EFFECT (ADR-040): haalt de benchmark van dit
    // effect de klantdrempel? De server weigert de publicatie zo niet; hier
    // wordt de knop uitgezet met dezelfde reden. Hier en niet in de twee
    // pagina's die de redactie tonen: dan kan er geen derde plek bijkomen die
    // hem vergeet.
    // AI-CONTEXT: See ../../helpers/intelligence/effectBenchmarkGate.js
    const benchmark = useEffectBenchmark({ msiId: (insight.Params && insight.Params.msiId) || '' })
    const blokkade = publicationBlockedReason(effectBenchmarkState(benchmark))

    const guard = (fn) => async (...args) => {
        try {
            await fn(...args)
        } catch (err) {
            // InsightCard toont zijn eigen fout; hier alleen niet stil laten vallen.
            console.error('InsightEditor:', err)
            throw err
        }
    }

    return (
        <>
            {/* Zonder themanaam (lijst nog niet geladen) geen knop: het gesprek
                zou anders 'Opzet inzicht: Thema laden…' heten. */}
            {insight.Status !== 'published' && themeTitle && (
                <div className="intelligence-toolbar">
                    <button
                        type="button"
                        onClick={() => startDecca(buildInsightDraftBrief({ insight, themeTitle }))}
                    >
                        Laat Decca een opzet maken
                    </button>
                </div>
            )}
            <InsightCard
                key={insight.ID}
                insight={insight}
                showOutput={showOutput}
                onSaveText={guard(({ sections, title, practice, leadTime }) => saveInsightDraft({ id: insight.ID, sections, title, practice, leadTime }))}
                onPublish={guard(({ sections, title, practice, leadTime }) => publishInsight({ id: insight.ID, sections, title, practice, leadTime }))}
                onUnpublish={guard(() => unpublishInsight({ id: insight.ID }))}
                onReject={guard(() => rejectInsight({ id: insight.ID }))}
                onDelete={guard(async () => { await deleteInsight({ id: insight.ID }); onDeleted() })}
                publishBlockedReason={blokkade}
            />
        </>
    )
}

export default InsightEditor
